/**
 * Auth Sagas
 */
import {push} from 'connected-react-router';
import React from 'react';

import {NotificationManager} from 'react-notifications';
import {replace} from 'react-router-redux';
import {all, call, fork, put, takeEvery} from 'redux-saga/effects';
import {
    logoutUserFailure,
    logoutUserSuccess,
    setRegistrationEmail,
    signinUserFailure,
    signinUserSuccess,
    signUpUserFailure,
    signUpUserSuccess,
} from '../actions';

import {
    LOGIN_FACEBOOK_USER,
    LOGIN_GITHUB_USER,
    LOGIN_GOOGLE_USER,
    LOGIN_TWITTER_USER,
    LOGIN_USER,
    LOGIN_WITH_EXTERNAL_SERVICE,
    LOGOUT_UNAUTHORIZED,
    LOGOUT_USER,
    SIGNUP_USER,
} from '../actions/types';
import {accessTokenLocalStorageKey, resourceCustomUsers, endpointLogin, endpointLoginWithExternalService, endpointLogout} from '../constants';
import {simpleDataProvider} from '../fetchApi';
import storage from '../fetchApi/storage';
import {FETCH} from '../fetchApi/types';
import IntlMessage from '../util/IntlMessage.tsx';

/**
 * Sign in user from /googlecallback with google Code
 * @param code
 * @return {Promise<unknown>}
 */
const signUpViaGoogleCode = async (code) =>
    simpleDataProvider(FETCH, `${resourceCustomUsers}/loginWithGoogle`, {code})
        .then(data => {
            const {accessToken} = data.data;
            return Promise.resolve(accessToken);
        })
        .catch(e => Promise.reject(e));

/**
 * Sign in user from /fbcallback with facebook Code
 * @param code
 * @param state
 * @return {Promise<unknown>}
 */
const signInViaFacebookCode = (code, state) =>
    simpleDataProvider(FETCH, `${resourceCustomUsers}/loginWithFacebook`, {code, state})
        .then(data => {
            const {accessToken} = data.data;
            return Promise.resolve(accessToken);
        })
        .catch(e => Promise.reject(e));

/**
 *
 * @param {String} code
 * @param {String} state
 * @param {String} provider
 * @return {Promise<Object>}
 */
const signInViaExternalService = (code, state, provider) =>
    simpleDataProvider(
        FETCH,
        `${resourceCustomUsers}/${endpointLoginWithExternalService}`,
        {code, provider, state},
    )
        .then(data => {
            const {accessToken, info} = data.data;
            return Promise.resolve({accessToken, info});
        })
        .catch(e => Promise.reject(e));

/**
 * Sigin User With Email and Password Request
 */
const signInUserWithEmailPasswordRequest = (email, password, captchaToken) =>
    simpleDataProvider(FETCH, `${resourceCustomUsers}/${endpointLogin}`, {email, password, captchaToken})
        .then(data => Promise.resolve(data.data))
        .catch((e) => Promise.reject(e.message || e || 'Login failed'));

/**
 * Signout Request
 */
const signOutRequest = (accessToken) => {
    if (accessToken) {
        return simpleDataProvider(FETCH, `${resourceCustomUsers}/${endpointLogout}`, {accessToken})
            .then(data => Promise.resolve(data.data))
            .catch(() => Promise.resolve());
    }
};

/**
 * Create User
 */
const registerUser = (username, email, password, captchaToken) =>
    simpleDataProvider(FETCH, `${resourceCustomUsers}/register`, {username, email, password, captchaToken})
        .then(data => {
            return Promise.resolve(data.data);
        })
        .catch(error => {
            return Promise.reject(error);
        });

/**
 * Signin User With Email & Password
 */
function* signInUserWithEmailPassword({payload}) {
    const {email, password, captchaToken} = payload.user;
    const {history} = payload;

    try {
        const accessToken = yield call(signInUserWithEmailPasswordRequest, email, password, captchaToken);
        if (accessToken) {
            const {ttl, ...data} = accessToken;
            storage.save(accessTokenLocalStorageKey, data, ttl);

            yield put(signinUserSuccess(accessToken.userId));

            history.push('/');
            NotificationManager.success(<IntlMessage id='auth.actions.success_sign_in' />);
        }
    } catch (error) {
        yield put(signinUserFailure(error));
    }
}

/**
 * Signin User With Facebook Account
 */
function* signinUserWithFacebookAccount({payload}) {
    try {
        const accessToken = yield call(signInViaFacebookCode, payload.code, payload.state);
        if (accessToken) {
            const {ttl, ...data} = accessToken;
            storage.save(accessTokenLocalStorageKey, data, ttl);

            yield put(signinUserSuccess(accessToken.userId));

            payload.history.push('/');
            NotificationManager.success(<IntlMessage id='auth.actions.success_sign_in' />);
        } else {
            yield put(signinUserFailure(''));
        }
    } catch (e) {
        // NotificationManager.error(e.message);
        yield put(signinUserFailure(e.message));
        setTimeout(() => window.location.assign('/'), 2000);
    }
}

/**
 * Signin User With Google Account
 */
function* signinUserWithGoogleAccount({payload}) {
    try {
        const accessToken = yield call(signUpViaGoogleCode, payload.code);
        if (accessToken) {
            const {ttl, ...data} = accessToken;
            storage.save(accessTokenLocalStorageKey, data, ttl);

            yield put(signinUserSuccess(accessToken.userId));

            payload.history.push('/');
            NotificationManager.success(<IntlMessage id='auth.actions.success_sign_in' />);
        } else {
            yield put(signinUserFailure(''));
        }
    } catch (e) {
        // NotificationManager.error(e.message);
        yield put(signinUserFailure(e.message));
        setTimeout(() => window.location.assign('/'), 2000);
    }
}

/**
 * Signin User With Twitter Account
 */
// function* signinUserWithTwitterAccount({payload}) {
//     try {
//         const signUpUser = yield call(signInUserWithTwitterRequest);
//         if (signUpUser.message) {
//             yield put(signinUserFailure(signUpUser.message));
//         } else {
//             localStorage.setItem('user_id', signUpUser.uid);
//             yield put(signinUserSuccess(signUpUser));
//             payload.push('/');
//         }
//     } catch (error) {
//         yield put(signinUserFailure(error));
//     }
// }

function* signInUserWithExternalService({payload}) {
    const message = {};
    try {
        const {accessToken, info} = yield call(signInViaExternalService, payload.code, payload.state, payload.provider);
        if (info) {
            for (const [key, value] of Object.entries(info)) { //TODO rework
                message.title = key[0].toUpperCase() + key.slice(1);
                message.value = value;
                break;
            }
        }

        if (accessToken) {
            const {ttl, ...data} = accessToken;
            storage.save(accessTokenLocalStorageKey, data, ttl);

            yield put(signinUserSuccess(accessToken.userId));

            payload.history.push('/');
            NotificationManager.success(<IntlMessage id='auth.actions.success_sign_in' />);
        } else {
            yield put(signinUserFailure(''));
        }
    } catch (e) {
        // NotificationManager.error(e.message);
        yield put(signinUserFailure(e.message));
        setTimeout(() => window.location.assign('/'), 2000);
    }
    if (message && Object.keys(message).length) {
        NotificationManager.info(message.value, message.title, 100000);
    }
}

/**
 * Signin User With Github Account
 */
function* signinUserWithGithubAccount({payload}) {
    try {
        const signUpUser = yield call(signInUserWithGithubRequest);
        if (signUpUser.message) {
            yield put(signinUserFailure(signUpUser.message));
        } else {
            localStorage.setItem('user_id', signUpUser.uid);
            yield put(signinUserSuccess(signUpUser));
            payload.push('/');
        }
    } catch (error) {
        yield put(signinUserFailure(error));
    }
}

/**
 * Signout User
 */
function* signOut({redirectTo}) {
    try {
        const accessToken = storage.load(accessTokenLocalStorageKey);
        yield call(signOutRequest, accessToken);
        localStorage.removeItem('user_id');
        localStorage.removeItem(accessTokenLocalStorageKey);
        yield put(push(redirectTo || '/'));
        yield put(logoutUserSuccess());
    } catch (error) {
        yield put(logoutUserFailure());
    }
}

/**
 * Create User
 */
function* createUserWithEmailPassword({payload}) {
    const {email, password, username, captchaToken} = payload.user;
    try {
        const signUpUserAccessToken = yield call(registerUser, username, email, password, captchaToken);
        yield put(signUpUserSuccess(signUpUserAccessToken.userId));
        NotificationManager.success(<IntlMessage id='users.actions.registration_success' />, '', 5000);
        yield put(setRegistrationEmail(email));
    } catch (error) {
        yield put(signUpUserFailure(error.message));
    }
}

function* logoutUnauthorizedUser({payload}) {
    storage.remove(accessTokenLocalStorageKey);
    yield put(
        replace({
            pathname: payload.redirectTo || '/',
            // Thanks to routers pathname "/" for unauthorized users will be redirected to "/signin"
        }),
    );
}

/**
 * Signin User In Backend
 */
export function* signinUserInBackend() {
    yield takeEvery(LOGIN_USER, signInUserWithEmailPassword);
}

/**
 * Signin User With Facebook
 */
export function* signInWithFacebook() {
    yield takeEvery(LOGIN_FACEBOOK_USER, signinUserWithFacebookAccount);
}

/**
 * Signin User With Google
 */
export function* signInWithGoogle() {
    yield takeEvery(LOGIN_GOOGLE_USER, signinUserWithGoogleAccount);
}

export function* signInWithExternalService() {
    yield takeEvery(LOGIN_WITH_EXTERNAL_SERVICE, signInUserWithExternalService);
}

/**
 * Signin User With Twitter
 */
export function* signInWithTwitter() {
    yield takeEvery(LOGIN_TWITTER_USER, signinUserWithTwitterAccount);
}

/**
 * Signin User With Github
 */
export function* signInWithGithub() {
    yield takeEvery(LOGIN_GITHUB_USER, signinUserWithGithubAccount);
}

/**
 * Signout User From Backend
 */
export function* signOutUser() {
    yield takeEvery(LOGOUT_USER, signOut);
}

/**
 * Create User
 */
export function* createUserAccount() {
    yield takeEvery(SIGNUP_USER, createUserWithEmailPassword);
}

export function* logoutUnauthorized() {
    yield takeEvery(LOGOUT_UNAUTHORIZED, logoutUnauthorizedUser);
}

/**
 * Auth Root Saga
 */
export default function* rootSaga() {
    yield all([
        fork(signinUserInBackend),
        fork(signInWithFacebook),
        fork(signInWithGoogle),
        fork(signInWithExternalService),
        fork(logoutUnauthorized),
        // fork(signInWithTwitter),
        // fork(signInWithGithub),
        fork(signOutUser),
        fork(createUserAccount),
    ]);
}